import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ScoreMeter from "../components/ScoreMeter";
import VibeTag from "../components/VibeTag";
import { getToken } from "../lib/auth";
import { getNextJourneyPath } from "../config/journeyConfig";
import { track } from "../lib/analytics";

const getCompatibilityScore = (p) => {
  if (typeof p.compatibility === "number") return p.compatibility;
  if (p.scores) {
    const vals = Object.values(p.scores).filter((v) => typeof v === "number");
    if (vals.length) return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }
  return 75;
};

const ZODIAC_SIGNS = [
  { name: "Capricorn", end: [1, 19] },
  { name: "Aquarius", end: [2, 18] },
  { name: "Pisces", end: [3, 20] },
  { name: "Aries", end: [4, 19] },
  { name: "Taurus", end: [5, 20] },
  { name: "Gemini", end: [6, 20] },
  { name: "Cancer", end: [7, 22] },
  { name: "Leo", end: [8, 22] },
  { name: "Virgo", end: [9, 22] },
  { name: "Libra", end: [10, 22] },
  { name: "Scorpio", end: [11, 21] },
  { name: "Sagittarius", end: [12, 21] },
  { name: "Capricorn", end: [12, 31] },
];

const getZodiac = (month, day) => {
  if (!month || !day) return null;
  const m = Number(month);
  const d = Number(day);
  for (const s of ZODIAC_SIGNS) {
    if (m < s.end[0] || (m === s.end[0] && d <= s.end[1])) return s.name;
  }
  return "Capricorn";
};

const SCORE_ROWS = [
  { key: "values", label: "Values" },
  { key: "communication", label: "Communication" },
  { key: "chemistry", label: "Chemistry" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "futureGoals", label: "Future Goals" },
  { key: "effort", label: "Effort" },
];

export default function HeadToHead() {
  const navigate = useNavigate();
  const nextPath = getNextJourneyPath("/head-to-head") ?? "/bracket";

  const [remaining, setRemaining] = useState([]);
  const [toRemove, setToRemove] = useState(null);
  const [choice, setChoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = getToken();
        let shortlistIds = [];
        if (token) {
          const slRes = await fetch("/api/shortlist", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (slRes.ok) {
            const slData = await slRes.json();
            shortlistIds = slData.prospect_ids || [];
          }
        }

          if (shortlistIds.length >= 2 && token) {
            const res = await fetch("/api/matches/eligible", {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              const pool = (data.matches || [])
  .filter((p) => p && p.id && shortlistIds.includes(p.id))
  .filter((p) => p.name && p.name.trim().length > 0);
              if (pool.length >= 2) {
                setRemaining(pool.slice(0, 7));
                return;
              }
            }
          }
      } catch {
        /* API unavailable — show empty state */
      }
      setRemaining([]);
    };
    load().finally(() => setLoading(false));
  }, []);

  const isFinalTwo = remaining.length === 2;

  const handleRemove = () => {
    if (!toRemove) return;
    setRemaining((prev) => prev.filter((p) => p.id !== toRemove));
    setToRemove(null);
    setChoice(null);
  };

  const handleFinalSubmit = async () => {
    if (!choice) return;
    const loser = remaining.find((p) => p.id !== choice);
    track("h2h_completed", { winner_id: choice, loser_id: loser?.id });
    try {
      const token = getToken();
      if (token) {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };
        await fetch("/api/shortlist", {
          method: "POST",
          headers,
          body: JSON.stringify({ prospect_ids: [choice] }),
        });
        await fetch("/api/head-to-head", {
          method: "POST",
          headers,
          body: JSON.stringify({
            round_key: "final",
            winner_id: choice,
            loser_id: loser?.id,
          }),
        });
      }
    } catch {
      /* non-blocking */
    }
    navigate(nextPath);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-white/40">Loading…</p>
        </div>
      </AppShell>
    );
  }

  if (remaining.length < 2) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-white/40">You need at least 2 real matches to continue your journey.</p>
        </div>
      </AppShell>
    );
  }

  /* ── Final two ── */
  if (isFinalTwo) {
    const left = remaining[0];
    const right = remaining[1];

    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]/70 mb-2">
              Final Decision
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Refine your shortlist
            </h1>
            <p className="text-white/50 text-sm max-w-xs mx-auto">
              You've narrowed it down to two. Who makes the cut?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr] gap-4 items-start mb-10">
            {[left, right].map((p, idx) => {
              const videoSrc = p.video_url || p.video || p.intro_video_url;
              const zodiac = getZodiac(p.birth_month, p.birth_day);
              const score = getCompatibilityScore(p);
              const isChosen = choice === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setChoice(p.id)}
                  className={[
                    "relative rounded-2xl overflow-hidden aspect-[3/4] w-full cursor-pointer",
                    "transition-all duration-300 outline-none text-left",
                    isChosen
                      ? "ring-4 ring-[#C9A84C] scale-[1.02]"
                      : "ring-1 ring-white/10 hover:ring-white/30",
                    idx === 1 ? "md:order-3" : "md:order-1",
                  ].join(" ")}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {videoSrc && (
                    <video
                      ref={(el) => { if (el) el.muted = true; }}
                      src={videoSrc}
                      className="absolute inset-0 w-full h-full object-cover z-10"
                      autoPlay
                      loop
                      playsInline
                      preload="auto"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/50 to-transparent z-20" />
                  <div className="absolute bottom-4 left-5 right-5 z-30">
                    <p className="text-white font-semibold text-lg leading-tight mb-0.5">
                      {p.name}, {p.age}
                    </p>
                    {zodiac && (
                      <p className="text-[#C9A84C]/80 text-xs mb-2">{zodiac}</p>
                    )}
                    <p className="text-[#C9A84C] text-xs font-semibold mb-3">
                      {score}% match
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).slice(0, 3).map((tag) => (
                        <VibeTag key={tag} tag={tag} />
                      ))}
                    </div>
                  </div>
                  {isChosen && (
                    <div className="absolute inset-0 flex items-center justify-center z-40">
                      <span className="bg-[#C9A84C] text-[#05070d] text-sm font-bold px-4 py-2 rounded-full">
                        Selected
                      </span>
                    </div>
                  )}
                </button>
              );
            })}

            {/* VS + score comparison column */}
            <div className="flex flex-col items-center gap-3 py-8 md:order-2">
              <span className="text-2xl font-bold text-white/20 mb-2">VS</span>
              {SCORE_ROWS.map((row) => {
                const lv = left.scores?.[row.key] ?? getCompatibilityScore(left);
                const rv = right.scores?.[row.key] ?? getCompatibilityScore(right);
                return (
                  <div key={row.key} className="w-full text-center">
                    <p className="text-white/40 text-[10px] uppercase tracking-widest mb-1">
                      {row.label}
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <ScoreMeter value={lv} />
                      <span className="text-white/20 text-xs">·</span>
                      <ScoreMeter value={rv} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleFinalSubmit}
              disabled={!choice}
              className="px-10 py-3 rounded-full bg-gradient-to-r from-[#C9A84C] to-[#e6c97a] text-[#05070d] font-semibold text-sm disabled:opacity-40 transition-opacity hover:opacity-90"
            >
              Confirm My Choice
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  /* ── Elimination view ── */
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]/70 mb-2">
            Head to Head
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Who doesn't make the cut?
          </h1>
          <p className="text-white/50 text-sm">
            {remaining.length} remaining · Select one to remove
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
          {remaining.map((p) => {
            const isRemoving = toRemove === p.id;
            const videoSrc = p.video_url || p.video || p.intro_video_url;
            return (
              <button
                key={p.id}
                onClick={() => setToRemove(isRemoving ? null : p.id)}
                className={[
                  "relative rounded-xl overflow-hidden aspect-[3/4] cursor-pointer",
                  "transition-all duration-200 outline-none",
                  isRemoving
                    ? "ring-4 ring-red-500 scale-[0.97]"
                    : "ring-1 ring-white/10 hover:ring-white/30",
                ].join(" ")}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {videoSrc && (
                  <video
                    ref={(el) => { if (el) el.muted = true; }}
                    src={videoSrc}
                    className="absolute inset-0 w-full h-full object-cover z-10"
                    autoPlay
                    loop
                    playsInline
                    preload="auto"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent z-20" />
                <div className="absolute bottom-3 inset-x-3 z-30">
                  <p className="text-white font-semibold text-sm leading-tight">
                    {p.name}, {p.age}
                  </p>
                  <p className="text-[#C9A84C]/80 text-xs">
                    {getCompatibilityScore(p)}% match
                  </p>
                </div>
                {isRemoving && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-40">
                    <span className="text-white font-semibold text-sm">Remove?</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-center gap-4">
          {toRemove ? (
            <>
              <button
                onClick={() => {
                  setToRemove(null);
                  setChoice(null);
                }}
                className="px-6 py-2.5 rounded-full border border-white/20 text-white/70 text-sm hover:border-white/40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemove}
                className="px-6 py-2.5 rounded-full bg-red-500/80 text-white text-sm font-semibold hover:bg-red-500 transition-colors"
              >
                Remove {remaining.find((p) => p.id === toRemove)?.name}
              </button>
            </>
          ) : (
            <p className="text-white/30 text-sm">Tap a profile to remove them</p>
          )}
        </div>
      </div>
    </AppShell>
  );
}
